// =====================================================
// TOAST COMPONENT
// =====================================================
// Sistema de notificaciones toast con Portal
// =====================================================

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const toastConfig = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    iconColor: 'text-green-500',
    titleColor: 'text-green-800',
    messageColor: 'text-green-700',
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-500',
    titleColor: 'text-red-800',
    messageColor: 'text-red-700',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconColor: 'text-amber-500',
    titleColor: 'text-amber-800',
    messageColor: 'text-amber-700',
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-500',
    titleColor: 'text-blue-800',
    messageColor: 'text-blue-700',
  },
};

export function Toast({ id, type, title, message, duration = 5000, onClose }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);
  const config = toastConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onClose(id), 300);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 300);
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-lg border shadow-lg
        ${config.bgColor} ${config.borderColor}
        transform transition-all duration-300 ease-out
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
      `}
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${config.iconColor}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${config.titleColor}`}>{title}</p>
        {message && (
          <p className={`mt-1 text-sm ${config.messageColor}`}>{message}</p>
        )}
      </div>
      <button
        onClick={handleClose}
        className={`flex-shrink-0 p-1 rounded hover:bg-black/5 ${config.iconColor}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// =====================================================
// TOAST CONTAINER CON PORTAL
// =====================================================

interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContainerProps {
  toasts: ToastItem[];
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  // Usar portal para renderizar directamente en document.body
  return createPortal(
    <div 
      className="fixed top-4 right-4 flex flex-col gap-2 max-w-sm w-full pointer-events-auto"
      style={{ zIndex: 99999 }}
    >
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={onRemove}
        />
      ))}
    </div>,
    document.body
  );
}

// =====================================================
// HOOK useToast
// =====================================================

let toastId = 0;
const generateId = () => `toast-${++toastId}`;

type ToastListener = (toasts: ToastItem[]) => void;
let listeners: ToastListener[] = [];
let toastsState: ToastItem[] = [];

const notifyListeners = () => {
  listeners.forEach((listener) => listener([...toastsState]));
};

export const toast = {
  success: (title: string, message?: string, duration?: number) => {
    const id = generateId();
    toastsState = [...toastsState, { id, type: 'success', title, message, duration }];
    notifyListeners();
    return id;
  },
  error: (title: string, message?: string, duration?: number) => {
    const id = generateId();
    toastsState = [...toastsState, { id, type: 'error', title, message, duration: duration ?? 8000 }];
    notifyListeners();
    return id;
  },
  warning: (title: string, message?: string, duration?: number) => {
    const id = generateId();
    toastsState = [...toastsState, { id, type: 'warning', title, message, duration }];
    notifyListeners();
    return id;
  },
  info: (title: string, message?: string, duration?: number) => {
    const id = generateId();
    toastsState = [...toastsState, { id, type: 'info', title, message, duration }];
    notifyListeners();
    return id;
  },
  dismiss: (id: string) => {
    toastsState = toastsState.filter((t) => t.id !== id);
    notifyListeners();
  },
  dismissAll: () => {
    toastsState = [];
    notifyListeners();
  },
};

export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>(toastsState);

  useEffect(() => {
    listeners.push(setToasts);
    return () => {
      listeners = listeners.filter((l) => l !== setToasts);
    };
  }, []);

  return {
    toasts,
    remove: toast.dismiss,
    success: toast.success,
    error: toast.error,
    warning: toast.warning,
    info: toast.info,
    dismissAll: toast.dismissAll,
  };
}
